/* ==========================================================================
   WAYPOINT — custom inline SVG icon set. 1.75px stroke, rounded joins.
   No icon fonts, no emoji.
   ========================================================================== */

interface IconProps {
  size?: number;
  className?: string;
}

const common = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

export function RouteIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M6 8.5v3a4 4 0 0 0 4 4h4a4 4 0 0 1 4 4" />
    </svg>
  );
}

export function MapIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M9 4 3 6.5v14L9 18l6 2.5 6-2.5v-14L15 6.5 9 4Z" strokeLinejoin="round" />
      <path d="M9 4v14M15 6.5v14" />
    </svg>
  );
}

/** Scooty tab icon — a friendly chat bubble with a spark, matching the
 * app's 24px/1.75px stroke language (docs/ITERATION-2.md §4). */
export function ScootyIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M3.5 7A3 3 0 0 1 6.5 4h9A3 3 0 0 1 18.5 7v5.5a3 3 0 0 1-3 3H9.8l-3.9 3a.5.5 0 0 1-.8-.4v-2.6A3 3 0 0 1 3.5 12.6V7Z" />
      <path d="M19.5 3v3.2M18 4.6h3" strokeLinecap="round" />
    </svg>
  );
}

export function MemoriesIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <rect x="4" y="7" width="13" height="13" rx="2.5" />
      <path d="M8 3.5h9a2.5 2.5 0 0 1 2.5 2.5v9" />
      <circle cx="8.75" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <path d="M6.5 17 10 13.5l2.3 2.3L15 12.8l2 2.2" />
    </svg>
  );
}

/** The Log tab: a timeline — a spine of entries with a pin at the top, read as "the running
 *  record", distinct from RouteIcon's A-to-B path (Trips) and MemoriesIcon's photo frame. */
export function LogIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M7 4.5v15" />
      <circle cx="7" cy="7" r="2" />
      <circle cx="7" cy="17" r="2" />
      <path d="M12.5 7H20" />
      <path d="M12.5 10.5h5" />
      <path d="M12.5 17H20" />
      <path d="M12.5 20.5h5" />
    </svg>
  );
}

export function CompassIcon({ size = 24, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.8 9.2 13 13l-3.8 1.8L11 11l3.8-1.8Z" strokeLinejoin="round" />
    </svg>
  );
}

/** A page with a broadcast arc rising off its corner — a written thing going out, which is what
 *  a post is. Deliberately not an RSS mark (that reads as a feed reader, not "our blog") and not a
 *  globe (that reads as "the internet"). */
export function BlogIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M6 3.5h7.5L18 8v12.5H6z" strokeLinejoin="round" />
      <path d="M13 3.5V8h4.5" strokeLinejoin="round" />
      <path d="M9 12h6M9 15.5h4" />
    </svg>
  );
}

/** Three dots, the near-universal "there is more behind this" mark. Deliberately
 *  not a hamburger: a hamburger promises a full navigation drawer, and this is a
 *  short list of four places. */
export function MoreIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <circle cx="6" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="18" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Sliders/faders, not a cog — a cog reads as "system preferences" and this
 * tab is appearance + feature switches. Two tracks, two handles. */
export function SettingsIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M4 8h10M18 8h2M4 16h4M12 16h8" />
      <circle cx="16" cy="8" r="2.1" />
      <circle cx="10" cy="16" r="2.1" />
    </svg>
  );
}

/** The bucket list: a pin with a star at its heart — "a place, wanted". Same 24px/1.75px
 *  language as the rest of the set; the star is filled so it reads at 22px in the More tray. */
export function BucketListIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
      <path
        d="m12 6.4 1.24 2.5 2.76.4-2 1.95.47 2.75L12 12.7l-2.47 1.3.47-2.75-2-1.95 2.76-.4L12 6.4Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

export function ChevronIcon({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M9 5.5 16 12l-7 6.5" />
    </svg>
  );
}

/** Tag-shaped backspace glyph (outline + X) — replaces the '⌫' text glyph on
 * the Login PIN pad (docs/UI-SPEC.md bans emoji/glyph icons). */
export function BackspaceIcon({ size = 20, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} {...common}>
      <path d="M8.5 5.5h11a1.5 1.5 0 0 1 1.5 1.5v10a1.5 1.5 0 0 1-1.5 1.5h-11L3 12l5.5-6.5Z" strokeLinejoin="round" />
      <path d="M11 9.5 16 14.5M16 9.5 11 14.5" />
    </svg>
  );
}
